import { Checkbox, Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FixedSizeList as List } from "react-window";

import "./SelectableList.Component.scss";

interface Props {
  open?: boolean;
  title?: string;
  actionButtonText?: string;
  actionButtonOnClick?: () => void;
  items?: { value: string; title: string; hoverText?: string; disabled?: boolean; onClick?: () => void }[];
  selectedItemValue?: string;
  onItemSelected?: (value: string, title: string) => void;
  openHeight?: number | string;
  showCheckboxes?: boolean;
  showSelectAll?: boolean;
  isLoading?: boolean;
  loadingText1?: string;
  loadingText2?: string;
  showSearch?: boolean;
  useWindowing?: boolean;
  windowHeight?: number;
  windowWidth?: number;
  windowItemHeight?: number;
}

export function SelectableList(props: Props) {
  const { open, title, actionButtonText, actionButtonOnClick, items, selectedItemValue, onItemSelected, openHeight, useWindowing } = props;
  const _openHeight = openHeight ? (typeof openHeight === "number" ? openHeight + "px" : openHeight) : "100%";
  const itemsHeight = open ? _openHeight : "26px";
  const [selectedItems, setSelectedItems] = useState<string[]>(selectedItemValue ? selectedItemValue.split(",") : []);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const selectableItems = items?.filter((item) => !item.disabled) || [];

  useEffect(() => {
    setSelectedItems(selectedItemValue ? selectedItemValue.split(",") : []);
  }, [selectedItemValue]);

  useEffect(() => {
    if (props.showSearch) {
      clearSelectedItems();
    }
  }, []);

  const filteredItems = items?.filter(
    (item) => item.title.toLowerCase().includes(searchQuery.toLowerCase())
      || item.hoverText?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const clearSelectedItems = () => {
    setSelectedItems([]);
    callOnItemSelected("", "");
  };

  const handleSelectAll = () => {
    const selectableFilteredItems = filteredItems?.filter((item) => !item.disabled) || [];

    if (props.showSearch && filteredItems?.length) {
      if (selectableFilteredItems.length === selectedItems.length) {
        clearSelectedItems();
        return;
      }
      const filteredSelectedItems = selectableFilteredItems.map((item) => item.value).join(",");
      callOnItemSelected(filteredSelectedItems, "Selected");
      setSelectedItems(selectableFilteredItems.map((item) => item.value));
    } else {
      if (selectedItems?.length === selectableItems.length) {
        clearSelectedItems();
      } else {
        const allItems = selectableItems.map((item) => item.value).join(",");
        callOnItemSelected(allItems || "", "All");
        setSelectedItems(selectableItems.map((item) => item.value));
      }
    }
  };

  const callOnItemSelected = (value: string, title: string) => {
    if (onItemSelected) {
      onItemSelected(value, title);
    }
  };

  const Row = ({ index, style }) => {
    const option = filteredItems?.[index];
    if (!option) {
      return;
    }

    const isSelected = selectedItems.includes(option.value);
    const className = [isSelected ? "x selected" : "item", option.disabled ? "disabled" : ""].filter(Boolean).join(" ");

    const handleSetSelected = () => {
      if (isSelected) {
        const newSelectedItems = selectedItems.filter((item) => item !== option.value);
        setSelectedItems(newSelectedItems);
        callOnItemSelected(newSelectedItems.join(","), "Selected");
      } else {
        const newSelectedItems = [...selectedItems, option.value];
        setSelectedItems(newSelectedItems);
        callOnItemSelected(newSelectedItems.join(","), "Selected");
      }
    };

    return (
      <div key={option.value} className={className} style={style}>
        {props.showCheckboxes ? (
          <button
            className="w-full"
            onClick={handleSetSelected}>
            <Checkbox
              label={option.title}
              checked={isSelected}
              disabled={option.disabled}
              onCheckedChange={handleSetSelected}
            />
          </button>
        ) : (
          option.title
        )}
        {option.hoverText && <span className="itemHoverText">{option.hoverText}</span>}
      </div>
    );
  };

  return (
    <div className={open ? "SelectableList open" : "SelectableList closed"}>
      {title && actionButtonText && (
        <div className="header">
          {title && <div className="title">{title}</div>}
          {actionButtonText && (
            <div className="actionButton" onClick={actionButtonOnClick}>
              {actionButtonText}
            </div>
          )}
        </div>
      )}

      <div className="items" style={{ height: itemsHeight }}>
        {props.isLoading ? (
          <div className="loading">
            <Spinner />
            <span className="loadingText">{props.loadingText1}</span>
            <span className="loadingText">{props.loadingText2}</span>
          </div>
        ) : (
          <>
            {props.showSearch && (
              <div className="dropdownSearch">
                <input type="text" placeholder="Search..." onChange={(e) => setSearchQuery(e.target.value)} />
              </div>
            )}

            {props.showSelectAll && (
              <Checkbox label="Select All" checked={selectedItems?.length === selectableItems?.length} onCheckedChange={handleSelectAll} />
            )}
            {filteredItems &&
              (useWindowing ? (
                <List height={props.windowHeight} itemCount={filteredItems.length} itemSize={props.windowItemHeight} width={props.windowWidth}>
                  {Row}
                </List>
              ) : (
                filteredItems.map((option) => {
                  const isSelected = selectedItems.includes(option.value);
                  const className = isSelected ? "x selected" : "item";
                  return (
                    <div
                      key={option.value}
                      className={className}
                      onClick={() => {
                        if (option.onClick) {
                          option.onClick();
                        } else if (onItemSelected) {
                          onItemSelected(option.value, option.title);
                        }
                      }}
                    >
                      {props.showCheckboxes ? (
                        <Checkbox
                          label={option.title}
                          checked={isSelected}
                          onCheckedChange={() => {
                            if (isSelected) {
                              const newSelectedItems = selectedItems.filter((item) => item !== option.value);
                              setSelectedItems(newSelectedItems);
                              callOnItemSelected(newSelectedItems.join(","), "Selected");
                            } else {
                              const newSelectedItems = [...selectedItems, option.value];
                              setSelectedItems(newSelectedItems);
                              callOnItemSelected(newSelectedItems.join(","), "Selected");
                            }
                          }}
                        />
                      ) : (
                        option.title
                      )}
                      {option.hoverText && <span className="itemHoverText">{option.hoverText}</span>}
                    </div>
                  );
                })
              ))}
          </>
        )}
      </div>
    </div>
  );
}
